import { useMemo } from 'react';

import {
  CrmSearchFilter,
  CrmSearchRequestOptions,
  FilterOperator,
  FiltersState,
  HiddenFilter,
  SortPropertyOverride,
} from 'framework-listing-lib/types';

import { DEFAULT_PAGE_SIZE } from 'framework-listing-lib/constants/pagination';
import { ASC, DESC } from 'framework-listing-lib/constants/sorts';
import useQuickFilters from 'framework-listing-lib/hooks/useQuickFilters';
import {
  combineFilters,
  getAggregatedFilterGroups,
} from 'framework-listing-lib/utils/filters';
import {
  useDeletedObjects,
  useFilterGroups,
  useFiltersState,
  useHiddenFilter,
} from './useListingLibFilters';

import { useHiddenFolderFilter } from 'framework-listing-lib/internal/Folders/hooks/useFolderProps';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { useProperties } from '../internal/providers/PropertiesProvider';

function getDefaultParams(objectTypeId: string): CrmSearchRequestOptions {
  return {
    count: DEFAULT_PAGE_SIZE,
    offset: 0,
    objectTypeId,
    requestOptions: {
      allPropertiesFetchMode: 'latest_version',
      includeAllProperties: true,
    },
    filterGroups: [
      {
        filters: [],
      },
    ],
    query: '',
    sorts: [
      {
        property: 'hs_created_at',
        order: DESC,
      },
      {
        property: 'hs_object_id',
        order: ASC,
      },
    ],
  };
}

function overrideSorts({
  sort,
  sortPropertyOverride,
}: {
  sort: FiltersState['sort'];
  sortPropertyOverride?: SortPropertyOverride;
}): CrmSearchRequestOptions['sorts'] {
  if (!sortPropertyOverride) {
    return [sort];
  }

  const isV1Override =
    !sortPropertyOverride.propertyMap && !sortPropertyOverride.transform;

  if (isV1Override) {
    return [
      {
        ...sort,
        property: sortPropertyOverride[sort.property]?.sortKey || sort.property,
      },
    ];
  }

  const mappedSort = {
    ...sort,
    property:
      sortPropertyOverride.propertyMap?.[sort.property]?.sortKey ||
      sort.property,
  };

  if (sortPropertyOverride.transform) {
    return sortPropertyOverride.transform(mappedSort);
  }
  return [mappedSort];
}

/**
 * Creates fetch params object from the current filter state
 * @param {Object} filters
 */
function getFetchParams({
  deletedObjects,
  filterGroups,
  filtersState,
  hiddenFilter,
  includeHiddenFilter,
  quickFilters,
  sortPropertyOverride,
  timeZoneSource,
}: {
  deletedObjects: number[];
  filterGroups: CrmSearchFilter[];
  filtersState: FiltersState;
  hiddenFilter?: HiddenFilter;
  includeHiddenFilter: boolean;
  quickFilters: FilterOperator[];
  sortPropertyOverride?: SortPropertyOverride;
  timeZoneSource?: CrmSearchRequestOptions['timeZoneSource'];
}): Partial<CrmSearchRequestOptions> {
  const { associationPreviews, pagination, query, requestOptions, sort } =
    filtersState;
  const { count, offset } = pagination;
  const fetchParams: Partial<CrmSearchRequestOptions> = {
    count,
    filterGroups: getAggregatedFilterGroups({
      deletedObjects,
      filterGroups,
      hiddenFilter,
      includeHiddenFilter,
      quickFilters,
    }),
    offset,
    query,
    timeZoneSource,
  };

  if (Object.keys(sort).length) {
    fetchParams.sorts = overrideSorts({
      sort,
      sortPropertyOverride,
    });
  }

  if (!fetchParams.query) {
    fetchParams.query = '';
  }

  if (requestOptions) {
    fetchParams.requestOptions = requestOptions;
  }

  if (associationPreviews) {
    fetchParams.associationPreviews = associationPreviews;
  }

  return fetchParams;
}

interface HookArgs {
  /**
   * Default to true when not provided.
   */
  includeHiddenFilter?: boolean;
}

/**
 * Returns the current crm-search query used in the UI
 */
export default function useCrmSearchQuery(
  { includeHiddenFilter }: HookArgs = {
    includeHiddenFilter: true,
  }
) {
  'use memo';
  const objectTypeId = useObjectTypeId();
  const hiddenFilter = useHiddenFilter();
  const deletedObjects = useDeletedObjects();
  const filters = useFiltersState();
  const hiddenFolderFilter = useHiddenFolderFilter();
  const isFoldersMode = useIsFoldersMode();
  const filterGroups = useFilterGroups();
  const quickFilters = useQuickFilters();
  const { sortPropertyOverride } = useProperties();
  const timeZoneSource = 'USER';
  const combinedHiddenFilters = useMemo(
    () =>
      includeHiddenFilter
        ? {
            filterGroups: combineFilters(
              hiddenFilter ? hiddenFilter.filterGroups : [],
              isFoldersMode && hiddenFolderFilter
                ? hiddenFolderFilter.filterGroups
                : []
            ),
          }
        : undefined,
    [hiddenFilter, hiddenFolderFilter, includeHiddenFilter, isFoldersMode]
  );

  const fetchParams = useMemo(
    () =>
      getFetchParams({
        deletedObjects,
        filterGroups,
        filtersState: filters,
        hiddenFilter: combinedHiddenFilters,
        includeHiddenFilter: Boolean(includeHiddenFilter),
        quickFilters,
        sortPropertyOverride,
        timeZoneSource,
      }),
    [
      combinedHiddenFilters,
      deletedObjects,
      filterGroups,
      filters,
      includeHiddenFilter,
      quickFilters,
      sortPropertyOverride,
      timeZoneSource,
    ]
  );

  return useMemo(
    () => ({
      ...getDefaultParams(objectTypeId),
      ...fetchParams,
    }),
    [fetchParams, objectTypeId]
  );
}
